/**
 * App-wide color tokens.
 *
 * Centralises reusable colours so shared components (CustomTextField,
 * CustomSelect, CustomDatePicker, EnrAutoComplete, per-view filters, etc.)
 * stay in sync. Prefer importing from here over hardcoding hex values.
 */

/**
 * "Active filter" highlight palette — applied to form fields when a filter
 * value is set. Light and dark variants for background, border, and the
 * hover/focused border state.
 */
export const HIGHLIGHT_COLORS = {
  background: {
    light: "#f9eab6",
    dark: "rgba(255, 245, 157, 0.08)",
  },
  border: {
    light: "#d97706",
    dark: "#ffee58",
  },
  borderActive: {
    light: "#b45309",
    dark: "#fff59d",
  },
  // Floating label chip when the field is highlighted.
  label: {
    background: {
      light: "#fffde7",
      dark: "#282b1d",
    },
    color: {
      light: "#b45309",
      dark: "#fff59d",
    },
  },
} as const;

/**
 * Returns the highlight palette for the given theme mode.
 */
export const getHighlightColors = (mode: "light" | "dark" = "light") => ({
  background: HIGHLIGHT_COLORS.background[mode],
  border: HIGHLIGHT_COLORS.border[mode],
  borderActive: HIGHLIGHT_COLORS.borderActive[mode],
});

/**
 * Stronger "active filter" highlight — a more saturated fill with a thicker
 * warning-coloured border. Used where a bolder emphasis is wanted (e.g. the
 * Venue multi-select dialog). Keep separate from HIGHLIGHT_COLORS so each can
 * evolve independently.
 */
export const HIGHLIGHT_STRONG_COLORS = {
  /** Field + label chip background. */
  background: "#fff2c7",
  /** Notched outline border (maps to the theme warning colour). */
  border: "warning.main",
  /** Border width when highlighted. */
  borderWidth: "2px !important",
  /** Input text colour when highlighted. */
  inputColor: "#000000",
} as const;
